/**
 * FX Settings Component
 * Allows admin to configure FX markup percentage
 */
import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import toast from 'react-hot-toast';

const FXSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    markup_percentage: 5,
    enabled: true
  });
  const [rates, setRates] = useState(null);

  useEffect(() => {
    loadSettings();
    loadRates();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/fx/settings');
      if (response.success) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Failed to load FX settings:', error);
      toast.error('Failed to load FX settings');
    } finally {
      setLoading(false);
    }
  };

  const loadRates = async () => {
    try {
      const response = await api.get('/fx/rates');
      if (response.success) {
        setRates(response.data);
      }
    } catch (error) {
      console.error('Failed to load FX rates:', error);
    }
  };

  const handleSave = async () => {
    // Validate markup percentage
    if (settings.markup_percentage < 0 || settings.markup_percentage > 15) {
      toast.error('Markup percentage must be between 0% and 15%');
      return;
    }

    try {
      setSaving(true);
      const response = await api.put('/admin/fx/settings', {
        markup_percentage: parseFloat(settings.markup_percentage),
        enabled: settings.enabled
      });

      if (response.success) {
        toast.success('FX settings updated successfully. All users will see updated rates on next page load.');
        
        // Reload rates to show updated markup in admin panel
        loadRates();
        
        // Broadcast event to clear all client caches
        // This will force all open tabs/windows to refresh their rates
        if (typeof BroadcastChannel !== 'undefined') {
          const channel = new BroadcastChannel('fx_updates');
          channel.postMessage({ type: 'MARKUP_CHANGED', timestamp: Date.now() });
          channel.close();
        }
        
        // Also clear sessionStorage cache to force immediate refresh
        sessionStorage.removeItem('fx_preference_cache');
      }
    } catch (error) {
      console.error('Failed to save FX settings:', error);
      toast.error(error.message || 'Failed to save FX settings');
    } finally {
      setSaving(false);
    }
  };

  const handleRefreshRates = async () => {
    try {
      setSaving(true);
      const response = await api.post('/admin/fx/refresh-rates');
      if (response.success) {
        toast.success('FX rates refreshed successfully');
        loadRates();
      }
    } catch (error) {
      console.error('Failed to refresh rates:', error);
      toast.error('Failed to refresh FX rates');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jet-yellow"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">FX Rate Settings</h2>
        <p className="text-sm text-gray-600 mt-1">
          Configure currency exchange rate markup and manage FX system
        </p>
      </div>

      {/* Settings Card */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Markup Configuration</h3>

        {/* Enable/Disable FX System */}
        <div className="mb-6">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.enabled}
              onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
              className="w-5 h-5 text-jet-yellow border-gray-300 rounded focus:ring-jet-yellow"
            />
            <span className="ml-3 text-sm font-medium text-gray-900">
              Enable Multi-Currency System
            </span>
          </label>
          <p className="text-xs text-gray-500 mt-1 ml-8">
            When disabled, all users will see prices in USD only
          </p>
        </div>

        {/* Markup Percentage */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Markup Percentage
          </label>
          <div className="flex items-center gap-4">
            <input
              type="number"
              min="0"
              max="15"
              step="0.1"
              value={settings.markup_percentage}
              onChange={(e) => setSettings({ ...settings, markup_percentage: e.target.value })}
              className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-jet-yellow"
              disabled={!settings.enabled}
            />
            <span className="text-gray-700">%</span>
            <input
              type="range"
              min="0"
              max="15"
              step="0.1"
              value={settings.markup_percentage}
              onChange={(e) => setSettings({ ...settings, markup_percentage: e.target.value })}
              className="flex-1"
              disabled={!settings.enabled}
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Allowed range: 0% - 15%. This markup is applied on top of the base exchange rate.
          </p>
          <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded">
            <p className="text-xs text-blue-900">
              <strong>Example:</strong> If USD to SGD rate is 1.35 and markup is {settings.markup_percentage}%, 
              the final rate will be {(1.35 * (1 + settings.markup_percentage / 100)).toFixed(4)}
            </p>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex gap-3">
          <button
            onClick={handleSave}
            disabled={saving || !settings.enabled}
            className="px-6 py-2 bg-jet-yellow text-gray-900 font-semibold rounded-md hover:bg-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
          <button
            onClick={handleRefreshRates}
            disabled={saving || !settings.enabled}
            className="px-6 py-2 bg-gray-200 text-gray-900 font-semibold rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Refreshing...' : 'Refresh Rates Now'}
          </button>
        </div>
      </div>

      {/* Current Rates Display */}
      {rates && settings.enabled && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Current Exchange Rates</h3>
            <span className="text-xs text-gray-500">
              Last updated: {new Date(rates.timestamp * 1000).toLocaleString()}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(rates.rates || {}).map(([currency, rate]) => {
              const rateWithMarkup = rate * (1 + settings.markup_percentage / 100);
              return (
                <div key={currency} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-lg font-bold text-gray-900">{currency}</span>
                    <span className="text-xs text-gray-500">Base Rate</span>
                  </div>
                  <div className="text-2xl font-semibold text-gray-900 mb-1">
                    {rate.toFixed(4)}
                  </div>
                  <div className="text-sm text-gray-600">
                    With {settings.markup_percentage}% markup: <strong>{rateWithMarkup.toFixed(4)}</strong>
                  </div>
                  <div className="text-xs text-gray-500 mt-2">
                    $100 USD = {currency} {(100 * rateWithMarkup).toFixed(2)}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Important Notice */}
          <div className="mt-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
            <div className="flex">
              <div className="flex-shrink-0">
                <i className="fa fa-exclamation-triangle text-yellow-400"></i>
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-yellow-800">Important</h4>
                <div className="mt-2 text-sm text-yellow-700">
                  <ul className="list-disc list-inside space-y-1">
                    <li>Exchange rates are fetched hourly from Open Exchange Rates API</li>
                    <li>All actual payments are processed in USD</li>
                    <li>Currency conversion is for display purposes only</li>
                    <li>Changing markup will affect all future price displays</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* System Disabled Notice */}
      {!settings.enabled && (
        <div className="bg-gray-100 rounded-lg p-6 text-center">
          <i className="fa fa-info-circle text-4xl text-gray-400 mb-3"></i>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            Multi-Currency System Disabled
          </h3>
          <p className="text-sm text-gray-600">
            Enable the system above to configure markup and view exchange rates
          </p>
        </div>
      )}
    </div>
  );
};

export default FXSettings;
